import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArrowLeftIcon, SendIcon } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input, InputPassword } from '@/components/ui/input';

const amazonCodeCommitFormSchema = z.object({
  accountName: z.string().min(1, 'Enter a name for this integration'),
  username: z.string().min(1, 'Enter your Amazon CodeCommit username'),
  password: z.string().min(1, 'Enter your Amazon CodeCommit password'),
});

export type IAmazonCodeCommitForm = z.infer<typeof amazonCodeCommitFormSchema>;

type Props = {
  onSubmit: (data: IAmazonCodeCommitForm) => Promise<void>;
  onBack: () => void;
  accountName?: string;
  btnText?: React.ReactNode;
};

const AmazonCodeCommitForm = ({ onSubmit, onBack, accountName, btnText }: Props) => {
  const form = useForm<IAmazonCodeCommitForm>({
    resolver: zodResolver(amazonCodeCommitFormSchema),
    defaultValues: {
      accountName: accountName || '',
      username: '',
      password: '',
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="accountName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Integration name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g., My Amazon CodeCommit Org name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="your-username" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <InputPassword {...field} placeholder="Paste your password here" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <Button size="sm" variant="outline" className="sm:flex-1" onClick={onBack}>
            <ArrowLeftIcon /> View instructions
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            className="sm:flex-1"
          >
            {btnText ?? (
              <Fragment>
                Submit <SendIcon />
              </Fragment>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AmazonCodeCommitForm;
